import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  StarIcon,
  WalletIcon,
  GiftIcon,
} from "@phosphor-icons/react";

const fmtUSD = (cents) =>
  `$${(Math.max(0, cents || 0) / 100).toFixed(2)}`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Founder Wallet card — shown only to verified buyers with the Founder badge.
 * Pulls /api/me/founder-wallet for lifetime cashback + current balance.
 */
export default function FounderWalletCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/me/founder-wallet")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!data || !data.is_founder) return null;

  const isSeller = (data.account_type || "").includes("seller");
  // Sellers: pink-purple gradient + gold-pink pill.
  // Buyers: blue-white gradient + blue-white pill.
  const cardBg = isSeller
    ? "linear-gradient(135deg, #2a0d3a 0%, #4a1142 35%, #6e1c4d 70%, #8a2c54 100%)"
    : "linear-gradient(135deg, #0a3a5c 0%, #156fa3 35%, #4FC3F7 70%, #E8F4FF 100%)";
  const ringConic = isSeller
    ? "conic-gradient(from 0deg, rgba(255,224,138,0.0), rgba(255,122,198,0.45), rgba(255,184,90,0.45), rgba(255,224,138,0.0))"
    : "conic-gradient(from 0deg, rgba(232,244,255,0.0), rgba(180,220,255,0.55), rgba(79,195,247,0.55), rgba(232,244,255,0.0))";
  const pillClass = isSeller ? "bidora-founder-pill-seller" : "bidora-founder-pill-buyer";
  const labelClass = isSeller ? "text-amber-200/80" : "text-sky-100/85";
  const subLabelClass = isSeller ? "text-amber-100/80" : "text-sky-50/85";
  const muteLabelClass = isSeller ? "text-amber-200/60" : "text-sky-100/70";
  const linkClass = isSeller
    ? "text-amber-200 hover:text-white"
    : "text-sky-100 hover:text-white";
  const avatarHaloClass = isSeller
    ? "bidora-founder-avatar bg-gradient-to-br from-[#FFE08A] via-[#FF7AC6] to-[#FFE08A]"
    : "bidora-founder-avatar-buyer bg-gradient-to-br from-[#E8F4FF] via-[#4FC3F7] to-[#E8F4FF]";
  const walletIconColor = isSeller ? "text-[#5b1d4f]" : "text-[#0a3a5c]";

  return (
    <div
      className="relative rounded-2xl p-6 mb-6 overflow-hidden bidorah-founder-wallet"
      style={{ background: cardBg }}
      data-testid="founder-wallet-card"
      data-founder-variant={isSeller ? "seller" : "buyer"}
    >
      {/* Slow-spinning shimmer ring */}
      <div className="absolute -inset-[2px] rounded-2xl pointer-events-none bidorah-founder-ring"
           style={{
             background: ringConic,
             filter: "blur(3px)",
             zIndex: 0,
           }} />
      {/* Diagonal "foil sweep" — catches light every 8s */}
      <div className="bidorah-founder-spark" style={{ zIndex: 1 }} />
      <div className="relative z-10 flex items-start justify-between flex-wrap gap-5">
        <div>
          <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full ${pillClass} mb-3`}
               data-testid="founder-wallet-badge">
            <StarIcon size={12} weight="fill" />
            <span className="text-[10px]">Founder Member</span>
          </div>
          <p className={`text-[10px] tracking-[0.22em] uppercase ${labelClass} font-bold`}>
            Founder Wallet
          </p>
          <p className="text-4xl md:text-5xl font-display font-black text-white mt-1 tracking-tighter"
             data-testid="founder-wallet-balance">
            {fmtUSD(data.balance_cents)}
          </p>
          <p className={`text-sm ${subLabelClass} mt-1`}>
            Auto-applies on your next checkout — no code needed.
          </p>
          <div className="flex gap-6 mt-4 text-xs">
            <div>
              <p className={`${muteLabelClass} uppercase tracking-wider font-bold`}>Lifetime earned</p>
              <p className="text-white font-bold mt-0.5" data-testid="founder-lifetime-cashback">
                {fmtUSD(data.lifetime_cashback_cents)}
              </p>
            </div>
            {data.founder_since && (
              <div>
                <p className={`${muteLabelClass} uppercase tracking-wider font-bold`}>Founder since</p>
                <p className="text-white font-bold mt-0.5">
                  {fmtDate(data.founder_since)}
                </p>
              </div>
            )}
            <div>
              <p className={`${muteLabelClass} uppercase tracking-wider font-bold`}>Cashback rate</p>
              <p className="text-white font-bold mt-0.5">2% (capped $25/order)</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className={`h-14 w-14 rounded-2xl ${avatarHaloClass} grid place-items-center`}>
            <WalletIcon size={26} weight="fill" className={walletIconColor} />
          </div>
          <Link
            to="/live"
            className={`inline-flex items-center gap-1.5 mt-2 text-xs font-bold ${linkClass} transition`}
            data-testid="founder-wallet-shop"
          >
            <GiftIcon size={14} weight="fill" />
            Shop now to earn more →
          </Link>
        </div>
      </div>

      {data.recent_entries && data.recent_entries.length > 0 && (
        <div className="relative z-10 mt-5 pt-4 border-t border-white/10">
          <p className={`text-[10px] uppercase tracking-wider ${muteLabelClass} font-bold mb-2`}>
            Recent cashback
          </p>
          <ul className="space-y-1.5">
            {data.recent_entries.slice(0, 5).map((e, i) => (
              <li
                key={`${e.id || e.order_id || i}`}
                className="flex justify-between text-xs"
              >
                <span className="text-white/80">
                  {fmtDate(e.created_at)} · order{" "}
                  <span className="font-mono text-white/60">
                    {(e.order_id || "").slice(-8)}
                  </span>
                </span>
                <span className="text-emerald-300 font-bold font-mono">
                  +{fmtUSD(e.delta_cents)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
